import type { PayloadAction } from "@reduxjs/toolkit";
import type { Directory } from "@southfields-digital/mxmz-react-ui";
import type { Id } from "react-toastify";
import type { AllEffect, CallEffect, PutEffect, SelectEffect } from "redux-saga/effects";
import { all, call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { MEDIA_LIBRARY_FILES_PAGE_SIZE } from "src/config/mediaLibrary";
import api from "src/services/graphicsApi";
import type { CopyFilesActionPayload } from "src/types/mediaLibrary";
import { calculateFileMD5Base64 } from "src/utils/fileHash";
import { v4 as uuidv4 } from "uuid";

import type { StateType } from "../reducers";
import {
	actions,
	type BulkUpload,
	type DirectoriesResponseType,
	type FilesResponseType,
	MEDIA_LIBRARY,
	NOTIFY_UPLOADS_COMPLETED,
	REQUEST_COPY_FILES,
	REQUEST_CREATE_BULK_FILE_UPLOAD,
	REQUEST_CREATE_DIRECTORY,
	REQUEST_DELETE_DIRECTORY,
	REQUEST_DELETE_FILE,
	REQUEST_GET_COPY_DIALOG_DIRECTORIES,
	REQUEST_GET_DIRECTORIES,
	REQUEST_GET_FILE,
	REQUEST_GET_FILES,
	REQUEST_RENAME_FILE,
	REQUEST_SEARCH_DIRECTORIES,
	REQUEST_SEARCH_FILES,
	REQUEST_UPDATE_DIRECTORY,
	REQUEST_UPLOAD_FILE,
} from "../reducers/mediaLibrary";
import { NOTIFY, UPDATE_NOTIFY } from "../reducers/notification";

function getAsNestedDirectories(flatDirectories: Directory[]): Directory[] {
	const [currentDirectory, ...remainingDirectories] = flatDirectories;

	return remainingDirectories.length === 0
		? [
				{
					...currentDirectory,
					directories: [],
				},
			]
		: [
				{
					...currentDirectory,
					directories: getAsNestedDirectories(remainingDirectories),
				},
			];
}

function* createBulkFileUpload(
	action: PayloadAction<{
		directoryId: string;
		fileList: FileList;
		callback?: (uploadedFileCount: number) => void;
	}>,
): Generator<
	AllEffect<CallEffect<void>> | CallEffect<File[][]> | CallEffect | PutEffect | SelectEffect,
	void,
	any
> {
	const bulkUploadId = uuidv4();
	const { directoryId, fileList, callback } = action.payload;
	let toastId;

	yield put({
		type: NOTIFY,
		getToastId: (id: Id) => (toastId = id),
		payload: {
			variant: "info",
			title: "Upload started",
			progress: 0,
		},
		toastOptions: {
			autoClose: false,
			closeButton: false,
		},
	});

	// Wait for toastId to be set
	while (!toastId) {
		yield delay(10);
	}

	yield put(
		actions.createBulkUpload({
			bulkUploadId,
			toastId,
			totalNumberOfFiles: action.payload.fileList.length,
		}),
	);

	// Start all uploads in parallel
	const uploadTasks = Array.from(fileList).map((file: File) =>
		call(createFileUpload, {
			bulkUploadId,
			directoryId,
			file,
		}),
	);

	// Execute all upload tasks in parallel
	yield all(uploadTasks);

	// Polling state to check if all uploads are completed
	let uploadCompleted = false;

	while (!uploadCompleted) {
		const { totalNumberOfFiles, completedUploads } = (yield select(
			(state: StateType) => state[MEDIA_LIBRARY].bulkUploads?.[bulkUploadId],
		)) as BulkUpload;

		// Update toast and notify backend of upload completion
		if (totalNumberOfFiles === completedUploads) {
			uploadCompleted = true;

			yield put({
				type: NOTIFY_UPLOADS_COMPLETED,
				payload: {
					bulkUploadId,
					callback,
				},
			});

			yield call(api.post, `/medialibrary/upload/complete`, {
				directoryId,
			});
		}

		// Wait a second before polling again
		yield delay(1000);
	}
}

function* copyFiles(
	action: PayloadAction<CopyFilesActionPayload>,
): Generator<CallEffect | PutEffect, void, any> {
	const { files, destinationDirectoryId, callback } = action.payload;

	let toastId;

	yield put({
		type: NOTIFY,
		getToastId: (id: Id) => (toastId = id),
		payload: {
			variant: "info",
			title: "Copying files...",
			progress: 0,
		},
		toastOptions: {
			autoClose: false,
			closeButton: false,
		},
	});

	// Wait for toastId to be set
	while (!toastId) {
		yield delay(10);
	}

	try {
		// Call the batch duplicate API endpoint with updated DTO structure
		yield call(api.post, `/medialibrary/files/duplicate`, {
			files,
			destinationDirectoryId,
		});

		// Mark copy operation as complete
		yield put(actions.receiveCopyFiles());

		// Show success notification
		yield put({
			type: UPDATE_NOTIFY,
			toastId,
			payload: {
				variant: "success",
				title: `Successfully copied ${files.length} ${files.length === 1 ? "file" : "files"}`,
				progress: 100,
			},
			toastOptions: {
				autoClose: 3000,
				closeButton: false,
			},
		});

		// Call the callback if provided
		if (callback) {
			yield call(callback, files.length);
		}
	} catch (error) {
		// Mark copy operation as failed
		yield put(actions.failedCopyFiles());

		// Show error notification
		yield put({
			type: UPDATE_NOTIFY,
			toastId,
			payload: {
				variant: "error",
				title: "Failed to copy files",
				progress: 0,
			},
			toastOptions: {
				autoClose: 5000,
				closeButton: true,
			},
		});
	}
}

function* createDirectory(
	action: PayloadAction<{ path: string; insertIntoDirectoryId: string; callback: () => void }>,
) {
	try {
		const { path, insertIntoDirectoryId, callback } = action.payload;
		const { data } = yield call(api.post, `/medialibrary/directory`, { path });

		yield put(actions.receiveCreateDirectory({ data, insertIntoDirectoryId }));
		yield call(callback);
	} catch (error) {
		yield put(actions.failedCreateDirectory());
	}
}

/**
 * Handles the initial upload request for a file in the media library.
 *
 * This function calculates the MD5 hash of the file and requests a presigned upload URL from the backend.
 * The MD5 hash is included in the presigned URL signature to ensure the uploaded content matches what was requested.
 * The backend is responsible for generating the presigned URL and creating a DB record for the file (with uploading=true).
 *
 * After the upload completes, the backend (NestJS) is responsible for synchronizing the file metadata and updating the DB.
 * In production, this is handled by an AWS Lambda triggered by S3 events.
 * In local/offline development, the backend triggers the sync directly after upload (see mediaLibrary.service.ts).
 *
 * The frontend does not need to call any sync endpoint or update the DB directly after upload.
 */
function* createFileUpload({
	bulkUploadId,
	directoryId,
	file,
}: {
	bulkUploadId: string;
	directoryId: string;
	file: File;
}): Generator<CallEffect<string> | CallEffect<unknown> | PutEffect, void, any> {
	try {
		// Calculate MD5 hash for file integrity verification
		const contentMd5Hash: string = yield call(calculateFileMD5Base64, file);

		const { data } = yield call(api.post, `/medialibrary/upload`, {
			size: file.size,
			name: file.name,
			type: file.type,
			directoryId,
			contentMd5Hash,
		});
		const { id, mediaObjectId, presignedUrl } = data;

		yield put({
			type: REQUEST_UPLOAD_FILE,
			payload: { id, bulkUploadId, directoryId, file, mediaObjectId, presignedUrl, contentMd5Hash },
		});
	} catch (error) {
		console.error(error);
	}
}

function* deleteDirectory(action: PayloadAction<{ id: string; callback: () => void }>) {
	try {
		const { id, callback } = action.payload;
		yield call(api.delete, `/medialibrary/directory/${id}`);

		yield put(actions.receiveDeleteDirectory({ id }));
		yield put({
			type: NOTIFY,
			payload: {
				variant: "success",
				title: "Directory deleted successfully!",
			},
		});
		yield call(callback);
	} catch (error) {
		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: "Something went wrong while deleting the directory!",
			},
		});
	}
}

function* deleteFile(action: PayloadAction<{ id: string }>) {
	const { id } = action.payload;

	try {
		yield call(api.delete, `/medialibrary/file/${id}`);
		yield put(actions.receiveDeleteFile({ id }));
	} catch (error) {
		yield put(actions.failedDeleteFile({ id }));
	}
}

function* renameFile(action: PayloadAction<{ id: string; name: string }>) {
	const { id, name } = action.payload;

	try {
		yield call(api.patch, `/medialibrary/file/${id}`, { name });
		yield put(actions.receiveRenameFile({ id, name }));
		yield put({
			type: NOTIFY,
			payload: {
				variant: "success",
				title: "File renamed successfully",
			},
		});
	} catch (error) {
		yield put(actions.failedRenameFile({ id }));
		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: "Failed to rename file",
			},
		});
	}
}

type MediaLibraryFileApiResponse = void | {
	data: {
		id: string;
		name: string;
		size: number;
		data: any[];
		createdAt: string;
		updatedAt: string;
	};
	status: number;
};
function* getFile(action: PayloadAction<{ id: string }>) {
	const { id } = action.payload;

	try {
		const response: MediaLibraryFileApiResponse = yield call(api.get, `/medialibrary/file/${id}`);

		if (response?.data) {
			yield put(actions.receiveGetFile({ file: response.data }));
		}
	} catch (error) {
		// Silently fail - the file may have been deleted or is no longer accessible
		console.error("Failed to fetch file:", error);
	}
}

type MediaLibraryDirectoriesApiResponse = void | { data: DirectoriesResponseType; status: number };
function* getDirectories(
	action: PayloadAction<{
		id: string;
		insertIntoDirectoryId: string;
		includeNested: boolean;
		initialPath: Directory[];
		searchTerm?: string;
	}>,
): Generator<PutEffect | CallEffect<any>, void, MediaLibraryDirectoriesApiResponse> {
	try {
		const { id, includeNested, initialPath } = action.payload;
		const queryParams: { [key: string]: string | number | boolean } = {
			...(includeNested ? { includeNested: true } : {}),
		};
		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${queryParams[key]}`)
			.join("&");

		const response = yield call(api.get, `/medialibrary/${id}/directories?${queryString}`);
		const apiResponse = response as MediaLibraryDirectoriesApiResponse;

		if (initialPath?.length > 1) {
			yield put(
				actions.setInitialDirectories({
					directories: getAsNestedDirectories(initialPath),
				}),
			);
		}

		if (apiResponse?.data) {
			yield delay(500);
			yield put(
				actions.receiveGetDirectories({ data: apiResponse?.data, insertIntoDirectoryId: id }),
			);
		}
	} catch (error) {
		yield put(
			actions.failedGetDirectories(
				((error as Error)?.message as string) || "Something went wrong loading directories!",
			),
		);
	}
}

type MediaLibraryFilesApiResponse = void | { data: FilesResponseType; status: number };
function* getFiles(
	action: PayloadAction<{
		id: string;
		pagination?: { offset: number; pageSize: number };
	}>,
): Generator<PutEffect | CallEffect<any>, void, MediaLibraryFilesApiResponse> {
	try {
		const { id, pagination } = action.payload;
		const queryParams: { [key: string]: string | number | boolean } = {
			offset: pagination?.offset || 0,
			pageSize: pagination?.pageSize || MEDIA_LIBRARY_FILES_PAGE_SIZE,
		};
		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${queryParams[key]}`)
			.join("&");
		const response = yield call(api.get, `/medialibrary/${id}/files?${queryString}`);
		const apiResponse = response as MediaLibraryFilesApiResponse;

		if (apiResponse?.data) {
			const shouldAppendResults = (pagination?.offset || 0) > 0;
			yield delay(500);
			yield put(actions.receiveGetFiles({ data: apiResponse.data, shouldAppendResults }));
		}
	} catch (error) {
		yield put(
			actions.failedGetFiles(
				((error as Error)?.message as string) || "Something went wrong loading media gallery!",
			),
		);
	}
}

function* notifyUploadsCompleted(
	action: PayloadAction<{ bulkUploadId: string; callback?: (uploadedFileCount: number) => void }>,
) {
	const { bulkUploadId, callback } = action.payload;
	const { successfulUploads, toastId } = (yield select(
		(state: StateType) => state[MEDIA_LIBRARY].bulkUploads?.[bulkUploadId],
	)) as BulkUpload;

	yield delay(2500);
	yield put({
		type: UPDATE_NOTIFY,
		toastId,
		payload: {
			variant: "success",
			title: "Upload completed successfully!",
			progress: 100,
		},
		toastOptions: {
			autoClose: 5000,
			closeButton: false,
		},
	});
	yield put(actions.deleteBulkUpload({ bulkUploadId }));

	if (callback) {
		yield call(callback, successfulUploads);
	}
}

type MediaLibrarySearchDirectoriesApiResponse = void | {
	data: DirectoriesResponseType;
	status: number;
};
function* searchDirectories(
	action: PayloadAction<{
		id: string;
		insertIntoDirectoryId: string;
		includeNested: boolean;
		initialPath: Directory[];
		searchTerm?: string;
	}>,
): Generator<PutEffect | CallEffect<any>, void, MediaLibrarySearchDirectoriesApiResponse> {
	try {
		yield delay(500);
		const { id, includeNested, searchTerm } = action.payload;
		const queryParams: { [key: string]: string | number | boolean } = {
			...(searchTerm ? { search: searchTerm } : {}),
			...(includeNested ? { includeNested: true } : {}),
		};
		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${queryParams[key]}`)
			.join("&");
		const response = yield call(api.get, `/medialibrary/${id}/directories?${queryString}`);
		const apiResponse = response as MediaLibraryDirectoriesApiResponse;

		if (apiResponse?.data) {
			yield delay(500);
			yield put(actions.receiveSearchDirectories({ data: apiResponse?.data }));
		}
	} catch (error) {
		yield put(
			actions.failedSearchDirectories(
				((error as Error)?.message as string) || "Something went wrong loading directories!",
			),
		);
	}
}

type MediaLibrarySearchFilesApiResponse = void | { data: FilesResponseType; status: number };
function* searchFiles(
	action: PayloadAction<{
		id: string;
		includeNested: boolean;
		pagination?: { offset: number; pageSize: number };
		searchTerm?: string;
	}>,
): Generator<PutEffect | CallEffect<any>, void, MediaLibrarySearchFilesApiResponse> {
	try {
		yield delay(500);
		const { id, includeNested, pagination, searchTerm } = action.payload;
		const queryParams: { [key: string]: string | number | boolean } = {
			offset: pagination?.offset || 0,
			pageSize: pagination?.pageSize || MEDIA_LIBRARY_FILES_PAGE_SIZE,
			...(searchTerm ? { search: searchTerm } : {}),
			...(includeNested ? { includeNested: true } : {}),
		};
		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${queryParams[key]}`)
			.join("&");
		const response = yield call(api.get, `/medialibrary/${id}/files?${queryString}`);
		const apiResponse = response as MediaLibrarySearchFilesApiResponse;

		if (apiResponse?.data) {
			const shouldAppendResults = (pagination?.offset || 0) > 0;
			yield delay(500);
			yield put(actions.receiveSearchFiles({ data: apiResponse.data, shouldAppendResults }));
		}
	} catch (error) {
		yield put(
			actions.failedSearchFiles(
				((error as Error)?.message as string) || "Something went wrong loading media gallery!",
			),
		);
	}
}

function* updateDirectory(
	action: PayloadAction<{ name: string; id: string; onSuccess: () => void; onFailure: () => void }>,
) {
	const { name, id, onFailure, onSuccess } = action.payload;

	try {
		const { data } = yield call(api.patch, `/medialibrary/directory/${id}`, {
			name,
		});

		yield put(actions.receiveUpdateDirectory({ data }));

		if ("function" === typeof onSuccess) {
			yield call(onSuccess);
		}

		yield put({
			type: NOTIFY,
			payload: {
				variant: "success",
				title: "Directory renamed successfully! Processing may take some time.",
			},
		});
	} catch (error) {
		if ("function" === typeof onFailure) {
			yield call(onFailure);
		}

		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: "Something went wrong while renaming the directory!",
			},
		});
	}
}

/**
 * Performs the actual file upload to the presigned URL provided by the backend.
 *
 * This is a direct PUT/POST to S3 (or compatible storage) and does not go through the backend API.
 * The Content-MD5 header must match the MD5 hash signed into the presigned URL for upload integrity.
 *
 * After upload, the backend is responsible for post-upload processing and DB sync.
 * In local/offline mode, the backend will detect the upload and trigger a sync automatically.
 *
 * The frontend only tracks upload progress and notifies the user; it does not need to trigger any further sync.
 */
function* uploadFile(
	action: PayloadAction<{
		presignedUrl: string;
		file: File;
		bulkUploadId: string;
		contentMd5Hash: string;
	}>,
): Generator<CallEffect<Response> | PutEffect | SelectEffect, void, any> {
	const { presignedUrl, file, bulkUploadId, contentMd5Hash } = action.payload;

	// Upload the file to the presigned URL
	let uploadSuccess = false;
	try {
		const headers: Record<string, string> = {
			"Content-Type": file.type,
		};

		// Add Content-MD5 header for integrity verification (must match presigned URL)
		if (contentMd5Hash) {
			headers["Content-MD5"] = contentMd5Hash;
		}

		const response = yield call(fetch, presignedUrl, {
			method: "PUT",
			body: file,
			headers,
		});

		if (!response.ok) {
			throw new Error(`Upload failed with status: ${response.status}`);
		}

		uploadSuccess = response.ok;
	} catch (error) {
		console.error("File upload failed:", error);
	} finally {
		yield put(actions.incrementCompletedUploads({ bulkUploadId }));

		if (uploadSuccess) {
			yield put(actions.incrementSuccessfulUploads({ bulkUploadId }));
		}

		const { successfulUploads, toastId, totalNumberOfFiles, uploadProgress } = (yield select(
			(state: StateType) => state[MEDIA_LIBRARY].bulkUploads?.[bulkUploadId],
		)) as BulkUpload;

		// Set toast notification
		yield put({
			type: UPDATE_NOTIFY,
			toastId,
			payload: {
				variant: "info",
				title: `Uploaded ${successfulUploads} of ${totalNumberOfFiles} files (${Math.round(
					uploadProgress,
				)}%)`,
				progress: uploadProgress,
			},
			toastOptions: {
				autoClose: false,
				closeButton: false,
			},
		});
	}
}

function* getCopyDialogDirectories(
	action: PayloadAction<{
		id: string;
	}>,
): Generator<PutEffect | CallEffect<any> | AllEffect<CallEffect<any>>, void, any> {
	try {
		const { id } = action.payload;

		// Fetch directories the same way DirectoryBrowser does
		const response = yield call(api.get, `/medialibrary/${id}/directories`);
		const apiResponse = response as MediaLibraryDirectoriesApiResponse;

		if (apiResponse?.data && apiResponse.data.directories) {
			// Fetch one level deeper for each directory to determine if they have children
			// This allows us to show/hide the chevron correctly
			const childDirectoriesResponses: MediaLibraryDirectoriesApiResponse[] = yield all(
				apiResponse.data.directories.map((dir: Directory) =>
					call(api.get, `/medialibrary/${dir.id}/directories`),
				),
			);

			// Transform directories to include their children (or empty array if none)
			const directoriesWithChildren = apiResponse.data.directories.map(
				(dir: Directory, index: number) => {
					const childResponse = childDirectoriesResponses[index];
					const childDirs = childResponse?.data?.directories ?? [];
					return {
						...dir,
						// If has children, set to undefined (lazy load on expand)
						// If no children, set to empty array (no chevron)
						directories: childDirs.length > 0 ? undefined : [],
					};
				},
			);

			yield put(
				actions.receiveGetCopyDialogDirectories({
					data: { ...apiResponse.data, directories: directoriesWithChildren },
					insertIntoDirectoryId: id,
				}),
			);
		}
	} catch (error) {
		yield put(
			actions.failedGetCopyDialogDirectories(
				((error as Error)?.message as string) || "Something went wrong loading directories!",
			),
		);
	}
}

export default function* root() {
	yield takeEvery(REQUEST_CREATE_BULK_FILE_UPLOAD, createBulkFileUpload);
	yield takeEvery(REQUEST_COPY_FILES, copyFiles);
	yield takeEvery(REQUEST_GET_DIRECTORIES, getDirectories);
	yield takeEvery(REQUEST_GET_COPY_DIALOG_DIRECTORIES, getCopyDialogDirectories);
	yield takeEvery(REQUEST_GET_FILES, getFiles);
	yield takeEvery(REQUEST_UPLOAD_FILE, uploadFile);
	yield takeLatest(NOTIFY_UPLOADS_COMPLETED, notifyUploadsCompleted);
	yield takeLatest(REQUEST_CREATE_DIRECTORY, createDirectory);
	yield takeLatest(REQUEST_DELETE_DIRECTORY, deleteDirectory);
	yield takeEvery(REQUEST_DELETE_FILE, deleteFile);
	yield takeLatest(REQUEST_RENAME_FILE, renameFile);
	yield takeEvery(REQUEST_GET_FILE, getFile);
	yield takeLatest(REQUEST_SEARCH_DIRECTORIES, searchDirectories);
	yield takeLatest(REQUEST_SEARCH_FILES, searchFiles);
	yield takeLatest(REQUEST_UPDATE_DIRECTORY, updateDirectory);
}
